import { Component, Input, Output, EventEmitter, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { JJamesDropdownOption } from '../../../types/ui.types';
import { cn } from '../../../utils/class-utils';

@Component({
    selector: 'jjames-dropdown',
    standalone: true,
    imports: [CommonModule],
    template: `
    <div class="relative inline-block text-left">
      <!-- Trigger button -->
      <button
        type="button"
        [class]="triggerClasses"
        (click)="toggleDropdown()"
        [attr.aria-expanded]="isOpen"
        [attr.aria-haspopup]="true"
      >
        <span class="flex items-center">
          @if (selectedOption?.icon) {
            <i [class]="selectedOption?.icon + ' mr-2'"></i>
          }
          {{ selectedOption?.label || placeholder }}
        </span>
        <svg 
          class="ml-2 h-5 w-5 transition-transform" 
          [class.rotate-180]="isOpen"
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 20 20" 
          fill="currentColor"
        >
          <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>

      <!-- Dropdown menu -->
      @if (isOpen) {
        <div
          [class]="menuClasses"
          role="menu"
          [attr.aria-orientation]="'vertical'"
        >
          <div class="py-1" role="none">
            @for (option of options; track trackByValue($index, option)) {
              <button
                type="button"
                [class]="getOptionClasses(option)"
                [disabled]="option.disabled"
                role="menuitem"
                (click)="selectOption(option)"
              >
                <span class="flex items-center">
                  @if (option.icon) {
                    <i [class]="option.icon + ' mr-2'"></i>
                  }
                  {{ option.label }}
                </span>
                <!-- Checkmark for selected option -->
                @if (isSelected(option)) {
                  <svg
                    class="ml-auto h-5 w-5 text-blue-600"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                }
              </button>
            }
          </div>
        </div>
      }
    </div>
  `,
    styles: []
})
export class JJamesDropdownComponent {
    @Input() options: JJamesDropdownOption[] = [];
    @Input() selectedValue?: any;
    @Input() placeholder = 'Select an option';
    @Input() disabled = false;
    @Input() position: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right' = 'bottom-left';
    @Input() size: 'sm' | 'md' | 'lg' = 'md';

    @Output() selectionChanged = new EventEmitter<JJamesDropdownOption>();

    isOpen = false;

    constructor(private elementRef: ElementRef) { }

    get selectedOption(): JJamesDropdownOption | undefined {
        return this.options.find(option => option.value === this.selectedValue);
    }

    get triggerClasses(): string {
        const baseClasses = 'inline-flex w-full justify-between items-center rounded-md border border-gray-300 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors';
        const sizeClasses = this.getSizeClasses();
        const disabledClasses = this.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50';

        return cn(baseClasses, sizeClasses, disabledClasses);
    }

    get menuClasses(): string {
        const baseClasses = 'absolute z-10 mt-2 w-full rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none';
        const positionClasses = this.getPositionClasses();

        return cn(baseClasses, positionClasses);
    }

    private getSizeClasses(): string {
        const sizes = {
            sm: 'px-3 py-1.5 text-sm',
            md: 'px-3 py-2 text-sm',
            lg: 'px-4 py-2.5 text-base'
        };
        return sizes[this.size];
    }

    private getPositionClasses(): string {
        const positions = {
            'bottom-left': 'origin-top-left left-0',
            'bottom-right': 'origin-top-right right-0',
            'top-left': 'origin-bottom-left left-0 bottom-full mb-2',
            'top-right': 'origin-bottom-right right-0 bottom-full mb-2'
        };
        return positions[this.position];
    }

    getOptionClasses(option: JJamesDropdownOption): string {
        const baseClasses = 'w-full text-left px-4 py-2 text-sm flex items-center justify-between transition-colors';
        const stateClasses = option.disabled
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-gray-900 hover:bg-gray-100 hover:text-gray-900 cursor-pointer';
        const selectedClasses = this.isSelected(option) ? 'bg-blue-50 text-blue-900' : '';

        return cn(baseClasses, stateClasses, selectedClasses);
    }

    isSelected(option: JJamesDropdownOption): boolean {
        return option.value === this.selectedValue;
    }

    toggleDropdown(): void {
        if (!this.disabled) {
            this.isOpen = !this.isOpen;
        }
    }

    selectOption(option: JJamesDropdownOption): void {
        if (!option.disabled) {
            this.selectedValue = option.value;
            this.isOpen = false;
            this.selectionChanged.emit(option);
        }
    }

    trackByValue(index: number, option: JJamesDropdownOption): any {
        return option.value;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event): void {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.isOpen = false;
        }
    }
}